// src/App.js
import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme, CssBaseline, GlobalStyles } from '@mui/material';
import { SnackbarProvider, useSnackbar } from 'notistack';
import Auth from './Auth';
import Dashboard from './Dashboard';
import './App.css'; // Import the updated App.css for custom classes and variables

function App() {
  const [userId, setUserId] = useState(null);
  const [mode, setMode] = useState('light');
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const id = params.get('id');
    if (token && id) {
      localStorage.setItem('authToken', token);
      localStorage.setItem('rememberedUserId', id);
      setUserId(Number(id));
      // Clear params from URL for clean state
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Load persisted user ID
  useEffect(() => {
    try {
      const storedUserId = localStorage.getItem('rememberedUserId');
      if (storedUserId) {
        setUserId(parseInt(storedUserId, 10));
      }
    } catch (err) {
      console.error('Error loading stored user:', err);
      enqueueSnackbar('Error loading session. Please log in again.', { variant: 'error' });
    }
  }, []);

  // Load persisted theme
  useEffect(() => {
    try {
      const saved = localStorage.getItem('themeMode');
      if (saved === 'light' || saved === 'dark') {
        setMode(saved);
      }
    } catch (err) {
      console.error('Error loading theme:', err);
    }
  }, []);

  const handleLogin = (id) => {
    try {
      setUserId(id);
      localStorage.setItem('rememberedUserId', String(id));
    } catch (err) {
      console.error('Error saving user ID:', err);
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('rememberedUserId');
      localStorage.removeItem('authToken'); // Clear token if stored
      setUserId(null);
    } catch (err) {
      console.error('Error during logout:', err);
    }
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'light' ? '#4DD0E1' : '#00897B', // Light teal for light mode, dark teal for dark
          },
          secondary: {
            main: '#80DEEA', // Subtle teal highlight, consistent across modes
          },
          success: {
            main: '#66BB6A', // Green for completed statuses
          },
          info: {
            main: '#29B6F6', // Blue-teal for in-progress
          },
          warning: {
            main: '#FFA726', // Orange for pending/warnings
          },
          background: {
            default: mode === 'light' ? '#F5F5F5' : '#0f172a', // Light gray for light, dark slate for dark
            paper: mode === 'light' ? '#FFFFFF' : '#1e293b', // White cards in light, dark gray in dark
          },
          text: {
            primary: mode === 'light' ? '#333333' : '#FFFFFF', // Dark text in light, white in dark
            secondary: '#757575', // Neutral gray for secondary text
          },
        },
        shape: { borderRadius: 12 }, // Kept rounded corners
        typography: { fontFamily: '"Inter", system-ui, -apple-system, sans-serif' }, // Kept typography
      }),
    [mode]
  );

  const toggleMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    try {
      localStorage.setItem('themeMode', newMode);
      enqueueSnackbar(`Switched to ${newMode} mode`, { variant: 'info' });
    } catch (err) {
      console.error('Error saving theme:', err);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          '.MuiSnackbar-root': {
            bottom: 12,
          },
          '.MuiSnackbarContent-root': {
            background: 'rgba(15, 118, 110, 0.82)',
            color: '#fff',
            minHeight: 36,
            padding: '8px 12px',
            borderRadius: 10,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.25)',
            backdropFilter: 'blur(6px)',
            alignItems: 'center',
          },
          '.MuiSnackbarContent-message': {
            fontWeight: 600,
            letterSpacing: '0.01em',
          },
          '.SnackbarContainer-root': {
            alignItems: 'flex-end !important',
            padding: '0 12px 12px !important',
          },
          '.SnackbarItem-contentRoot': {
            background: 'rgba(15, 118, 110, 0.82) !important',
            color: '#fff !important',
            minHeight: '36px !important',
            padding: '8px 12px !important',
            borderRadius: '10px !important',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.25) !important',
            backdropFilter: 'blur(6px)',
            alignItems: 'center',
          },
          '.SnackbarItem-message': {
            fontWeight: '600 !important',
            letterSpacing: '0.01em !important',
          },
        }}
      />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={4000}
      >
        {!userId ? (
          <Auth onLogin={handleLogin} />
        ) : (
          <div className="App"> {/* Wrap Dashboard in App class for custom layout styles */}
            <Dashboard
              userId={userId}
              onLogout={handleLogout}
              onToggleMode={toggleMode}
            />
          </div>
        )}
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
